import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Lock, ArrowLeft } from "lucide-react";
import { site } from "../mock";
import { API, TOKEN_KEY } from "../lib/adminApi";
import ListTab from "../components/admin/ListTab";
import SettingsTab from "../components/admin/SettingsTab";
import BeforeAfterTab from "../components/admin/BeforeAfterTab";

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Incorrect username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0C0C0C] px-6">
      <form onSubmit={submit} className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="h-14 w-14 rounded-full border border-[#C6A15B]/50 flex items-center justify-center mb-4">
            <Lock className="text-[#C6A15B]" size={22} />
          </div>
          <h1 className="font-serif-display text-3xl text-[#F7F2EA]">Admin Login</h1>
          <p className="text-[#8A857A] text-sm mt-1">Skinplicity content manager</p>
        </div>
        <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" autoComplete="username" className="w-full bg-transparent border-b border-[#3a3a3a] focus:border-[#C6A15B] outline-none py-3 text-[#F7F2EA] placeholder-[#6b6b6b] mb-6" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password" className="w-full bg-transparent border-b border-[#3a3a3a] focus:border-[#C6A15B] outline-none py-3 text-[#F7F2EA] placeholder-[#6b6b6b] mb-6" />
        <button type="submit" disabled={loading} className="w-full bg-[#C6A15B] text-[#0C0C0C] text-xs tracking-[0.2em] uppercase py-4 hover:bg-[#d8b877] transition-colors disabled:opacity-60">
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-xs tracking-[0.2em] uppercase text-[#8A857A] hover:text-[#C6A15B]">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </form>
    </div>
  );
}

const TABS = [
  { key: "beforeafter", label: "Before & After" },
  { key: "pricing", label: "Pricing" },
  { key: "promotions", label: "Promotions" },
  { key: "team", label: "Team" },
  { key: "locations", label: "Locations" },
  { key: "settings", label: "Wording & Hours" },
];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("beforeafter");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginForm onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-[#F7F2EA]">
      <header className="bg-[#0C0C0C] px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <span className="font-serif-display text-2xl text-[#C6A15B]">{site.name} Admin</span>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-xs tracking-[0.18em] uppercase text-[#EDE7DA] hover:text-[#C6A15B]">View Site</Link>
          <button onClick={logout} className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#EDE7DA] hover:text-[#C6A15B]">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="bg-[#111111] px-6 overflow-x-auto">
        <div className="max-w-6xl mx-auto flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap px-5 py-4 text-[11px] tracking-[0.16em] uppercase transition-colors border-b-2 ${
                tab === t.key ? "text-[#C6A15B] border-[#C6A15B]" : "text-[#8A857A] border-transparent hover:text-[#EDE7DA]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {tab === "beforeafter" && <BeforeAfterTab onAuthError={logout} />}
        {tab === "pricing" && (
          <ListTab
            collection="pricing"
            title="Pricing"
            subtitle="Add, edit or remove treatments and prices shown on the homepage."
            titleField="name"
            fields={[
              { name: "name", label: "Treatment", type: "text", placeholder: "e.g. Dermal Fillers" },
              { name: "price", label: "Price", type: "text", placeholder: "e.g. From $300" },
              { name: "unit", label: "Unit", type: "text", placeholder: "e.g. per half syringe" },
            ]}
          />
        )}
        {tab === "promotions" && (
          <ListTab
            collection="promotions"
            title="Promotions"
            subtitle="Active promotions appear in a section on the homepage. Set to Hidden to take one down without deleting."
            titleField="title"
            fields={[
              { name: "title", label: "Title", type: "text", placeholder: "e.g. $50 Off First Filler" },
              { name: "badge", label: "Badge (optional)", type: "text", placeholder: "e.g. New Client" },
              { name: "description", label: "Description", type: "textarea", placeholder: "Details of the offer" },
              { name: "active", label: "Status", type: "toggle" },
            ]}
          />
        )}
        {tab === "team" && (
          <ListTab
            collection="team"
            title="Team"
            subtitle="Add or remove team members. Leave the photo empty to show gold initials."
            titleField="name"
            fields={[
              { name: "name", label: "Name", type: "text", placeholder: "e.g. Mandy Race" },
              { name: "role", label: "Role", type: "text", placeholder: "e.g. APRN" },
              { name: "photo", label: "Photo", type: "image" },
            ]}
          />
        )}
        {tab === "locations" && (
          <ListTab
            collection="locations"
            title="Locations"
            subtitle="Each location shows an embedded map. 'Map query' should be a full address for an exact pin."
            titleField="name"
            fields={[
              { name: "name", label: "Location Name", type: "text" },
              { name: "address", label: "Address", type: "text" },
              { name: "phone", label: "Phone", type: "text" },
              { name: "mapQuery", label: "Map Query (address for exact pin)", type: "text" },
              { name: "directions", label: "Directions URL", type: "text" },
            ]}
          />
        )}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
